import {
  campaignDiscountLabel,
  formatCampaignDollars,
  type GiftPassBundle,
  type GiftPassPricingRule,
} from '@bonsai/shared';

export const ALL_BUNDLES = 'all';
export const BUNDLE_LIST_TABS = [ALL_BUNDLES, 'active', 'inactive'] as const;
export type BundleListTab = (typeof BUNDLE_LIST_TABS)[number];

export const BUNDLE_TAB_LABELS: Record<BundleListTab, string> = {
  all: 'All',
  active: 'Enabled',
  inactive: 'Disabled',
};

const inTab = (bundle: GiftPassBundle, tab: BundleListTab) =>
  tab === ALL_BUNDLES || bundle.isActive === (tab === 'active');

export function countBundlesByTab(
  bundles: readonly GiftPassBundle[],
): Record<BundleListTab, number> {
  const active = bundles.filter((b) => b.isActive).length;
  return { all: bundles.length, active, inactive: bundles.length - active };
}

/** Enabled before disabled, so what customers can buy is on top; then fewest passes first. */
const activeThenFewestPasses = (a: GiftPassBundle, b: GiftPassBundle) =>
  Number(b.isActive) - Number(a.isActive) ||
  a.passCount - b.passCount ||
  a.name.localeCompare(b.name);

export function visibleBundles(
  bundles: readonly GiftPassBundle[],
  tab: BundleListTab,
  search: string,
): GiftPassBundle[] {
  const needle = search.trim().toLowerCase();
  return bundles
    .filter((bundle) => inTab(bundle, tab))
    .filter((bundle) => bundle.name.toLowerCase().includes(needle))
    .sort(activeThenFewestPasses);
}

export function bundleCountLabel(count: number): string {
  return `${count} ${count === 1 ? 'bundle' : 'bundles'}`;
}

export function passCountLabel(passCount: number): string {
  return `${passCount} ${passCount === 1 ? 'pass' : 'passes'}`;
}

export function bundleDiscountLabel(
  bundle: Pick<GiftPassBundle, 'discountType' | 'discountValue'>,
) {
  return campaignDiscountLabel(bundle);
}

export function bundleEmptyState(
  tab: BundleListTab,
  search: string,
): { title: string; hint?: string } {
  const term = search.trim();
  if (term) {
    return {
      title: `No bundles match “${term}”`,
      hint:
        tab === ALL_BUNDLES
          ? 'Clear the search or try different words.'
          : 'Clear the search or try another filter.',
    };
  }
  if (tab === ALL_BUNDLES) return { title: 'No bundles yet' };
  return {
    title: `No ${BUNDLE_TAB_LABELS[tab].toLowerCase()} bundles`,
    hint: 'Try another filter.',
  };
}

export function pricingRuleText(rule: GiftPassPricingRule): string {
  const days = rule.windowDays === 1 ? 'day' : `${rule.windowDays} days`;
  return (
    `A pass costs the most common ticket price at public workshops within ` +
    `${rule.radiusMiles} miles of the buyer over the next ${days}, including any running ` +
    `ticket sales campaign. Where there are no workshops that close, a pass is ` +
    `${formatCampaignDollars(rule.fallbackPriceCents)}, less any running campaign. ` +
    `A bundle's discount comes off that price.`
  );
}
